import React from 'react';
import { Filter, Paperclip } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../ui/sheet';
import { inr, PAYMENT_META } from '../poPayment';
import { formatDate } from '../../../lib/dateUtils';

const APPROVAL_LABEL = { approved: 'Approved', pending: 'Awaiting approval', not_submitted: 'Not submitted', rejected: 'Approval rejected' };

export const matchDrill = (r, d) => d
  && (!d.client || r.client_id === d.client)
  && (!d.category || r.category === d.category)
  && (!d.vendor || r.vendor === d.vendor)
  && (!d.month || r.month === d.month);

export default function PODrilldownSheet({ drill, rows, onClose, onApply }) {
  const list = React.useMemo(() => (rows || []).filter((r) => matchDrill(r, drill)), [rows, drill]);
  const total = list.reduce((s, r) => s + r.total, 0);
  const byState = list.reduce((m, r) => ({ ...m, [r.payment_state]: (m[r.payment_state] || 0) + r.total }), {});

  return (
    <Sheet open={!!drill} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-lg bg-white overflow-y-auto" data-testid="po-drilldown-drawer">
        <SheetHeader>
          <SheetTitle className="text-[#1a1a2e] pr-6">{drill?.title}</SheetTitle>
          <SheetDescription>{list.length} PO{list.length !== 1 ? 's' : ''} · {inr(total)}</SheetDescription>
        </SheetHeader>
        <div className="grid grid-cols-3 gap-2 my-4">
          {['paid', 'pending', 'overdue'].map((k) => (
            <div key={k} className={`rounded-xl border px-3 py-2 ${PAYMENT_META[k].pill}`}>
              <p className="text-[10px] uppercase tracking-wider font-semibold">{PAYMENT_META[k].label}</p>
              <p className="text-sm font-bold tabular-nums">{inr(byState[k] || 0)}</p>
            </div>
          ))}
        </div>
        {(drill?.client || drill?.category || drill?.vendor) && (
          <button type="button" onClick={() => onApply(drill)}
            className="mb-4 w-full inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-fuchsia-700 border border-fuchsia-200 rounded-lg py-2 hover:bg-fuchsia-50"
            data-testid="po-drilldown-apply-filter"><Filter className="w-3.5 h-3.5" /> Filter the whole page to this</button>
        )}
        <ul className="space-y-2" data-testid="po-drilldown-list">
          {list.map((r) => (
            <li key={r._id} className="p-3 rounded-xl border border-slate-200/70 hover:border-fuchsia-200 transition-colors" data-testid={`po-drilldown-item-${r._id}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-sm text-[#1a1a2e]">{r.po_number}</span>
                <span className="font-semibold tabular-nums text-sm">{inr(r.total)}</span>
              </div>
              <p className="text-xs text-slate-500 truncate mt-0.5">{r.vendor} · {r.client_name} · {r.category}</p>
              <div className="flex flex-wrap items-center gap-1.5 mt-2 text-[11px]">
                <span className={`px-1.5 py-0.5 rounded-md border font-semibold ${PAYMENT_META[r.payment_state].pill}`}>{PAYMENT_META[r.payment_state].label}</span>
                <span className="px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-600">{APPROVAL_LABEL[r.approval_status] || r.approval_status}</span>
                {r.date && <span className="text-slate-500">{formatDate(r.date)}</span>}
                {r.margin_pct != null && <span className="text-slate-500">· {r.margin_pct}% margin</span>}
                {r.proof_count > 0 && <span className="inline-flex items-center gap-0.5 text-fuchsia-700"><Paperclip className="w-3 h-3" />{r.proof_count}</span>}
              </div>
            </li>
          ))}
        </ul>
      </SheetContent>
    </Sheet>
  );
}
